import React from "react";
import { CButton, CFormLabel, CFormSelect, CSpinner } from "@coreui/react";
import { CSpinnerText } from "@tradingdroid/frontends-components";
import type { RootState } from "@src/services/state/store";
import { useSelector } from "react-redux";

interface Props {
  // Current state
  currentPage: number;
  totalItems: number;
  perPage: number;
  itemLabel: string;

  // Immediate pagination actions
  onPerPageChange: (perPage: number) => void;
  onPrevPage: () => void;
  onNextPage: () => void;

  // Server-side pagination (optional)
  onNextRequest?: () => void; // Trigger server fetch for more data
  isLoading?: boolean;
  hasMore?: boolean; // Whether more items are available from server

  // Page size options
  defaultPageSize?: number; // Base page size for calculating options (1x, 2x, 3x)

  // Disable all controls
  disabled?: boolean;

  // Optional addon component for buttons like "New Backtest"
  addon?: React.ReactNode;

  // Shown beside the info text, e.g. why paging stopped early
  notice?: string;
}

export const PaginationControls: React.FC<Props> = ({
  currentPage,
  totalItems,
  perPage,
  itemLabel,
  onPerPageChange,
  onPrevPage,
  onNextPage,
  addon,
  notice,
  onNextRequest,
  isLoading = false,
  hasMore = true,
  defaultPageSize,
  disabled = false,
}) => {
  const isSyncing = useSelector((state: RootState) => state.layout.isSyncing);

  // Calculate pagination info
  const startItem = currentPage * perPage + 1;
  const endItem = Math.min((currentPage + 1) * perPage, totalItems);

  // Calculate page size options: default, default*2, default*3
  const baseSize = defaultPageSize ?? perPage;
  const pageSizeOptions = [baseSize, baseSize * 2, baseSize * 3];

  const handlePrevPage = () => {
    onPrevPage();
  };

  const handleNextPage = () => {
    // Check if we're at the end of current cached items
    const isAtEndOfCurrentItems = endItem >= totalItems;

    if (isAtEndOfCurrentItems && onNextRequest) {
      // Trigger server fetch for more items
      onNextRequest();
    }

    // Always advance to next page
    onNextPage();
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = parseInt(e.target.value);
    onPerPageChange(newLimit);
  };

  // Show "X+" when we don't know the final total
  const totalDisplay = hasMore ? `${totalItems}+` : totalItems;

  // Handle empty state
  const infoText =
    totalItems === 0
      ? `No ${itemLabel} found for the current filters`
      : `Showing ${startItem} - ${endItem} of ${totalDisplay} ${itemLabel}`;

  return (
    <div className="PaginationControls">
      <span className="PaginationControls-info">{infoText}</span>
      {notice && <span className="PaginationControls-notice text-warning">{notice}</span>}

      <div className="PaginationControls-controls">
        <CFormLabel htmlFor="paginationLimitSelect" className="me-2">
          Per Page:
        </CFormLabel>
        <CFormSelect
          id="paginationLimitSelect"
          size="sm"
          value={perPage}
          onChange={handleLimitChange}
          className="PaginationControls-perPageSelect"
          style={{ display: "inline-block", width: "80px" }}
          disabled={disabled}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </CFormSelect>

        <CButton
          onClick={handlePrevPage}
          disabled={disabled || currentPage === 0}
          size="sm"
          color="primary"
        >
          ← Prev
        </CButton>

        <CButton
          onClick={handleNextPage}
          disabled={disabled || isLoading || (endItem >= totalItems && !hasMore)}
          size="sm"
          color="primary"
        >
          {isLoading ? <CSpinnerText text="Next →" /> : "Next →"}
        </CButton>

        {isSyncing && <CSpinner size="sm" className="ms-2" />}

        {addon && <div className="PaginationControls-addon">{addon}</div>}
      </div>
    </div>
  );
};
